import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  Alert,
  Snackbar,
  LinearProgress,
  Tab,
  Tabs,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import LoopIcon from '@mui/icons-material/Loop';
import DownloadIcon from '@mui/icons-material/Download';

import SequenceCard from '../components/SequenceCard';
import SequenceForm from '../components/SequenceForm';
import * as api from '../services/api';

const SequencesView = ({ latestWebSocketMessage, isConnected }) => {
  const [loading, setLoading] = useState(true);
  const [sequences, setSequences] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editSequence, setEditSequence] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executingSequenceId, setExecutingSequenceId] = useState(null);
  const [completedExecution, setCompletedExecution] = useState(null);

  // Cargar datos iniciales
  useEffect(() => {
    fetchSequences();
    fetchExecutions();
  }, []);

  // Manejar mensajes de WebSocket
  useEffect(() => {
    if (!latestWebSocketMessage) return;

    console.log('WebSocket message received:', latestWebSocketMessage);

    switch (latestWebSocketMessage.type) {
      case 'sequenceExecutionStart':
        setSuccess(`Ejecución iniciada: ${latestWebSocketMessage.sequenceName}`);
        setIsExecuting(true);
        setExecutingSequenceId(latestWebSocketMessage.executionId);
        break;
      case 'sequenceExecutionProgress':
        setSuccess(`Progreso: ${latestWebSocketMessage.sequenceName} - Paso ${latestWebSocketMessage.currentStep} de ${latestWebSocketMessage.totalSteps}`);
        break;
      case 'sequenceExecutionComplete':
        setSuccess(`Ejecución completada: ${latestWebSocketMessage.sequenceName}`);
        setIsExecuting(false);
        setExecutingSequenceId(null);
        setCompletedExecution(latestWebSocketMessage);
        fetchExecutions();
        break;
      case 'sequenceExecutionError':
        setError(`Error en la ejecución: ${latestWebSocketMessage.error}`);
        setIsExecuting(false);
        setExecutingSequenceId(null);
        fetchExecutions();
        break;
      default:
        break;
    }
  }, [latestWebSocketMessage]);

  const fetchSequences = async () => {
    setLoading(true);
    try {
      const data = await api.fetchSequences();
      setSequences(data);
    } catch (error) {
      console.error('Error al cargar secuencias:', error);
      setError('Error al cargar las secuencias');
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutions = async () => {
    try {
      const data = await api.fetchSequenceExecutions();
      setExecutions(data);
    } catch (error) {
      console.error('Error al cargar historial de ejecuciones:', error);
      setError('Error al cargar el historial de ejecuciones');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenForm = () => {
    setEditSequence(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditSequence(null);
  };

  const handleEditSequence = (sequence) => {
    setEditSequence(sequence);
    setFormOpen(true);
  };

  const handleSaveSequence = async (sequenceData) => {
    try {
      if (editSequence) {
        await api.updateSequence(editSequence.id, sequenceData);
        setSuccess('Secuencia actualizada correctamente');
      } else {
        await api.createSequence(sequenceData);
        setSuccess('Secuencia creada correctamente');
      }
      fetchSequences();
    } catch (error) {
      console.error('Error al guardar secuencia:', error);
      setError(error.message || 'Error al guardar la secuencia');
    }
  };

  const handleDeleteSequence = async (id) => {
    try {
      await api.deleteSequence(id);
      setSuccess('Secuencia eliminada correctamente');
      fetchSequences();
    } catch (error) {
      console.error('Error al eliminar secuencia:', error);
      setError('Error al eliminar la secuencia');
    }
  };

  const handleExecuteSequence = async (id, file) => {
    try {
      setIsExecuting(true);
      setExecutingSequenceId(id);
      await api.executeSequence(id, file);
      setSuccess('Ejecución de secuencia iniciada');
    } catch (error) {
      console.error('Error al ejecutar secuencia:', error);
      setError('Error al ejecutar la secuencia');
      setIsExecuting(false);
      setExecutingSequenceId(null);
    }
  };

  const handleCloseError = () => {
    setError('');
  };

  const handleCloseSuccess = () => {
    setSuccess('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success.main';
      case 'failed':
        return 'error.main';
      case 'processing':
        return 'info.main';
      default:
        return 'text.secondary';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Secuencias
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenForm}
          >
            Nueva Secuencia
          </Button>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 3 }}
        >
          <Tab icon={<DescriptionIcon />} label="Mis Secuencias" />
          <Tab icon={<HistoryIcon />} label="Historial de Ejecuciones" />
        </Tabs>

        {/* Tab Panel: Secuencias */}
        {activeTab === 0 && (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : sequences.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" gutterBottom>
                  No hay secuencias definidas
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenForm}
                  sx={{ mt: 2 }}
                >
                  Crear primera secuencia
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {sequences.map((sequence) => (
                  <Grid item xs={12} md={6} key={sequence.id}>
                    <SequenceCard
                      sequence={sequence}
                      onEdit={handleEditSequence}
                      onDelete={handleDeleteSequence}
                      onExecute={handleExecuteSequence}
                      isExecuting={isExecuting && executingSequenceId === sequence.id}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* Tab Panel: Historial de Ejecuciones */}
        {activeTab === 1 && (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : executions.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1">
                  No hay ejecuciones de secuencias en el historial
                </Typography>
              </Paper>
            ) : (
              <Box>
                {executions.map((execution) => (
                  <Paper
                    key={execution.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderLeft: 4,
                      borderColor: getStatusColor(execution.status)
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">
                        {execution.sequenceName}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getStatusColor(execution.status),
                          fontWeight: 'bold'
                        }}
                      >
                        {execution.status === 'completed' ? 'Completado' :
                         execution.status === 'failed' ? 'Error' :
                         execution.status === 'processing' ? 'Procesando' : 'Pendiente'}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          ID: {execution.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Inicio: {new Date(execution.startTime).toLocaleString()}
                        </Typography>
                        {execution.endTime && (
                          <Typography variant="body2" color="text.secondary">
                            Fin: {new Date(execution.endTime).toLocaleString()}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Pasos: {execution.currentStep} / {execution.totalSteps}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Archivo entrada: {execution.inputFile && execution.inputFile.split('/').pop()}
                        </Typography>
                        {execution.outputFile && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            Archivo salida: {execution.outputFile.split('/').pop()}
                            <Tooltip title="Descargar archivo generado">
                              <IconButton 
                                size="small" 
                                color="primary"
                                href={execution.status === 'completed' ? api.getSequenceOutputDownloadUrl(execution.outputFile) : '#'}
                                disabled={execution.status !== 'completed'}
                                sx={{ ml: 1 }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    
                    {execution.status === 'processing' && (
                      <LinearProgress
                        variant="determinate"
                        value={(execution.currentStep / execution.totalSteps) * 100}
                        sx={{ mt: 2 }}
                      />
                    )}
                    
                    {execution.error && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {execution.error}
                      </Alert>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Form for creating/editing sequences */}
      <SequenceForm
        open={formOpen}
        onClose={handleCloseForm}
        onSave={handleSaveSequence}
        editSequence={editSequence}
      />

      {/* Snackbars for error and success messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Notificación de descarga disponible cuando se completa una ejecución */}
      <Snackbar
        open={!!completedExecution}
        autoHideDuration={10000}
        onClose={() => setCompletedExecution(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setCompletedExecution(null)} 
          severity="info" 
          sx={{ width: '100%' }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<DownloadIcon />}
              href={completedExecution && completedExecution.outputFile 
                ? api.getSequenceOutputDownloadUrl(completedExecution.outputFile) 
                : '#'}
            >
              Descargar
            </Button>
          }
        >
          Archivo generado disponible para descarga
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SequencesView; 